import type { SelectProps as AriaSelectProps, ValidationResult } from "react-aria-components";
import { Button, ListBox, Select, SelectValue } from "react-aria-components";
import type { ComponentPropsWithRef } from "react";
import React from "react";
import { tv } from "tailwind-variants";

import type { BaseItem, GroupedItem } from "@/core/types";
import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { cn } from "@/lib/helpers/cn";

import { FieldError2 } from "../atoms/FieldError";
import { Label2 } from "../atoms/Label";
import { ListBoxItem2 } from "../atoms/ListBoxItem";
import { ListBoxSection2 } from "../atoms/ListBoxSection";
import { Popover2 } from "../atoms/Popover";
import { Text2 } from "../atoms/Text";

type FlatSelectProps<T extends BaseItem> = {
  mode?: "flat";
  options: T[];
};

type GroupedSelectProps<T extends BaseItem> = {
  mode: "grouped";
  options: GroupedItem<T>[];
};

type BaseSelectProps<T extends BaseItem> = FlatSelectProps<T> | GroupedSelectProps<T>;

type SelectProps<T extends BaseItem> = {
  className?: string;
  description?: string;
  errorMessage?: ((validation: ValidationResult) => string) | string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: string;
  placeholder?: string;
} & BaseSelectProps<T> &
  Omit<AriaSelectProps<T>, "children"> &
  Pick<ComponentPropsWithRef<"select">, "autoComplete" | "form" | "name">;

export type { SelectProps };

export function Select2<T extends BaseItem = BaseItem>({
  className,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  mode = "flat",
  name,
  options,
  placeholder,
  ...props
}: SelectProps<T>) {
  const { button, icon, listBox, select, selectValue } = selectStyles();

  const renderItems = () => {
    switch (mode) {
      case "grouped":
        return (item: GroupedItem<T>) => (
          <ListBoxSection2 header={item.name} items={item.children} key={item.id}>
            {(child) => (
              <ListBoxItem2 id={child.id} textValue={child.name}>
                {({ isSelected }) => (
                  <>
                    {child.name}
                    {isSelected && <CheckmarkIcon />}
                  </>
                )}
              </ListBoxItem2>
            )}
          </ListBoxSection2>
        );

      case "flat":
        return (item: T) => (
          <ListBoxItem2 id={item.id} textValue={item.name}>
            {({ isSelected }) => (
              <>
                {item.name}
                {isSelected && <CheckmarkIcon />}
              </>
            )}
          </ListBoxItem2>
        );

      default:
        mode satisfies never;

        return null;
    }
  };

  return (
    <Select
      className={cn(select(), className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      name={name}
      {...props}
    >
      <Label2 isInvalid={isInvalid} isRequired={isRequired} label={label} />

      <Button className={cn(button(), "atom-focus group")}>
        <ChevronIcon className={icon()} />

        <SelectValue className={selectValue()}>
          {({ selectedText }) => selectedText || placeholder || "Select..."}
        </SelectValue>
      </Button>

      {description && !isInvalid && <Text2 slot="description" text={description} />}

      <FieldError2>{errorMessage}</FieldError2>

      <Popover2>
        <ListBox className={listBox()} items={options as T[]}>
          {renderItems() as (item: T) => React.ReactNode}
        </ListBox>
      </Popover2>
    </Select>
  );
}

export const selectStyles = tv({
  defaultVariants: {
    size: "md",
  },
  slots: {
    button: cn(
      "tw:flex tw:h-11 tw:flex-row-reverse tw:items-center tw:justify-between tw:p-2.5",
      "tw:gap-x-2 tw:text-sm",
      "tw:rounded-sm! tw:border tw:border-solid tw:border-stroke-tertiary tw:bg-fill-white",
      "tw:cursor-pointer tw:rounded",
      "tw:transition-all tw:duration-150",
      "tw:hover:border-stroke-secondary",
      "tw:data-disabled:border-stroke-tertiary tw:data-disabled:bg-fill-senary",
      "tw:disabled:cursor-not-allowed tw:disabled:text-text-quinary",
      "tw:group-data-invalid:border-stroke-error-primary",
    ),
    icon: cn(
      "tw:h-6 tw:w-6 tw:text-fill-secondary",
      "tw:transition-transform tw:duration-200",
      "tw:group-data-open:rotate-180",
    ),
    listBox: cn("tw:max-h-75 tw:overflow-y-auto", "tw:outline-none", "tw:py-1"),
    select: "tw:group tw:flex tw:flex-col tw:gap-1.5 tw:w-full",
    selectValue: cn("tw:text-left tw:text-sm", "tw:data-placeholder:text-text-quinary"),
  },
});
